import { Device } from "@fairfleet/geotab";
import { useMemo } from "react";
import { useDecodeVins } from "@/hooks/devices/useDecodeVins";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";

/**
 * Make and model per vehicle, from the decoded VIN — shown on all three views.
 *
 * @returns The decoded type by device id, and the error of the decoding if it failed — without
 * it the column silently stays empty.
 */
export function useVinDecoded(devices: Device[]) {
  const vins = useMemo(
    () =>
      (devices as DeviceWithVehicleInfo[])
        .map(device => device.vehicleIdentificationNumber)
        .filter((vin): vin is string => !!vin),
    [devices],
  );
  const { data: details, error } = useDecodeVins(vins);

  const byDeviceId = useMemo(() => {
    const byVin = new Map((details ?? []).map(detail => [detail.vin, detail]));
    const result = new Map<string, string>();

    for (const device of devices as DeviceWithVehicleInfo[]) {
      const vin = device.vehicleIdentificationNumber;
      const detail = vin ? byVin.get(vin) : undefined;
      const decoded = detail ? [detail.make, detail.model].filter(Boolean).join(" ") : "";
      if (decoded) {
        result.set(device.id, decoded);
      }
    }

    return result;
  }, [devices, details]);

  return { byDeviceId, error };
}
